import { useState } from "react";
import {
  api, type PendingDevice, type IpRule, type ApiTokenRow, type User,
} from "../lib/api.js";
import { Confirm, Empty, Notice, Tabs, useAsync } from "../components/ui.js";
import { dateTime, relativeTime } from "../lib/format.js";
import type { PageProps } from "./Shell.js";

/**
 * The controls that sit in front of everything else.
 *
 * Whether a machine has to be let in, where connections may come from, how
 * long one may last, and the tokens that let a script drive this server
 * without borrowing somebody's login.
 */
export default function Security({ me, onToggleRail }: PageProps) {
  const [tab, setTab] = useState<"machines" | "network" | "alerts" | "tokens">("machines");
  const can = (perm: string) =>
    me.permissions.includes("*") || me.permissions.includes(perm)
    || me.permissions.includes(perm.split(":")[0] + ":*");

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Security</h1>
          <span className="topbar__hint">Who gets in, from where, and for how long</span>
        </div>
      </header>

      <div className="content">
        <div className="stack">
          <Tabs
            tabs={[
              { id: "machines" as const, label: "Machines waiting" },
              { id: "network" as const, label: "Where from" },
              { id: "alerts" as const, label: "Alerts" },
              { id: "tokens" as const, label: "Automation" },
            ]}
            active={tab}
            onChange={setTab}
          />
          {tab === "machines" ? <PendingMachines canWrite={can("device:write")} /> : null}
          {tab === "network" ? <Network canWrite={can("acl:write")} /> : null}
          {tab === "alerts" ? <Alerts canWrite={can("settings:write")} /> : null}
          {tab === "tokens" ? <Tokens canWrite={can("settings:write")} /> : null}
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------ waiting machines --

function PendingMachines({ canWrite }: { canWrite: boolean }) {
  const pending = useAsync(() => api.get<{ data: PendingDevice[] }>("/api/console/devices/pending"), []);
  const settings = useAsync(
    () => api.get<{ settings: Record<string, string> }>("/api/console/settings"), []);
  const [rejecting, setRejecting] = useState<PendingDevice | null>(null);
  const [busy, setBusy] = useState("");
  const required = settings.data?.settings?.require_device_approval === "1";

  const setRequired = async (on: boolean) => {
    await api.post("/api/console/settings", { require_device_approval: on ? "1" : "0" });
    settings.reload();
  };

  return (
    <>
      {pending.error ? <Notice>{pending.error}</Notice> : null}

      <div className="panel">
        <div className="panel__body">
          <label className="row" style={{ gap: "0.5rem", alignItems: "flex-start" }}>
            <input
              type="checkbox"
              checked={required}
              disabled={!canWrite || settings.loading}
              onChange={(e) => void setRequired(e.target.checked)}
              style={{ marginTop: "0.25rem" }}
            />
            <span>
              <strong>Make a new machine wait to be let in</strong>
              <div className="small faint">
                Without this, anything holding the server key appears here and can be
                connected to as soon as it starts. Turning it on affects machines that
                arrive from now on; everything already enrolled keeps working.
              </div>
            </span>
          </label>
        </div>
      </div>

      {!pending.loading && !pending.data?.data.length ? (
        <Empty
          title="Nothing waiting"
          hint={required
            ? "A new machine will appear here when it first connects."
            : "Machines are let in automatically. Turn the setting above on to review them first."}
        />
      ) : (
        <div className="panel">
          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Machine</th><th>Address</th><th>Version</th>
                  <th>First seen</th><th style={{ width: 170 }} />
                </tr>
              </thead>
              <tbody>
                {(pending.data?.data ?? []).map((d) => (
                  <tr key={d.guid}>
                    <td>
                      {d.hostname || d.id}
                      <div className="small faint">
                        {d.id}{d.os ? ` · ${d.os}` : ""}{d.username ? ` · ${d.username}` : ""}
                      </div>
                    </td>
                    <td className="mono small">{d.ip || "—"}</td>
                    <td className="muted small">{d.version || "—"}</td>
                    <td className="muted small">
                      {d.first_seen ? relativeTime(d.first_seen) : "—"}
                    </td>
                    <td className="actions">
                      {canWrite ? (
                        <>
                          <button
                            className="btn btn--sm btn--primary"
                            disabled={busy === d.guid}
                            onClick={async () => {
                              setBusy(d.guid);
                              try {
                                await api.post(`/api/console/devices/${d.guid}/approve`);
                                pending.reload();
                              } finally { setBusy(""); }
                            }}
                          >
                            Let it in
                          </button>
                          <button className="btn btn--sm btn--ghost" onClick={() => setRejecting(d)}>
                            Refuse
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rejecting ? (
        <Confirm
          title={`Refuse ${rejecting.hostname || rejecting.id}?`}
          body="It is removed from the list. If it connects again you will be asked about it again."
          confirmLabel="Refuse"
          onCancel={() => setRejecting(null)}
          onConfirm={async () => {
            await api.post(`/api/console/devices/${rejecting.guid}/reject`);
            setRejecting(null);
            pending.reload();
          }}
        />
      ) : null}
    </>
  );
}

// --------------------------------------------------------- where from ----

function Network({ canWrite }: { canWrite: boolean }) {
  const rules = useAsync(() => api.get<IpRule[]>("/api/console/ip-rules"), []);
  const users = useAsync(() => api.get<{ data: User[] }>("/api/console/users?pageSize=500"), []);
  const settings = useAsync(
    () => api.get<{ settings: Record<string, string> }>("/api/console/settings"), []);

  const [cidr, setCidr] = useState("");
  const [subject, setSubject] = useState("");
  const [allow, setAllow] = useState(true);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [probe, setProbe] = useState("");
  const [verdict, setVerdict] = useState<{ allow: boolean; reason: string } | null>(null);
  const [minutes, setMinutes] = useState("");

  const limit = settings.data?.settings?.max_session_minutes ?? "";

  const add = async () => {
    setError("");
    try {
      await api.post("/api/console/ip-rules", {
        cidr,
        subject_type: subject ? "user" : "all",
        subject_id: subject,
        allow,
        note,
      });
      setCidr(""); setNote("");
      rules.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <>
      {rules.error ? <Notice>{rules.error}</Notice> : null}
      {error ? <Notice>{error}</Notice> : null}

      <div className="notice">
        With no rules at all, connections are accepted from anywhere. Add one allow
        rule and everything outside it is refused &mdash; so add the range you are
        sitting in first. A deny rule always wins, which lets you exclude one range
        without listing every other.
      </div>

      {canWrite ? (
        <div className="panel">
          <div className="panel__body">
            <div className="row row--wrap" style={{ gap: "0.5rem" }}>
              <input
                className="input" style={{ maxWidth: 200 }}
                placeholder="10.0.0.0/8 or an address"
                value={cidr} onChange={(e) => setCidr(e.target.value)}
              />
              <select
                className="input" style={{ maxWidth: 220 }}
                value={subject} onChange={(e) => setSubject(e.target.value)}
              >
                <option value="">Everyone</option>
                {(users.data?.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              <select
                className="input" style={{ maxWidth: 130 }}
                value={allow ? "allow" : "deny"}
                onChange={(e) => setAllow(e.target.value === "allow")}
              >
                <option value="allow">Allow</option>
                <option value="deny">Block</option>
              </select>
              <input
                className="input" style={{ flex: 1, minWidth: 140 }}
                placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)}
              />
              <button className="btn btn--primary btn--sm" disabled={!cidr.trim()} onClick={() => void add()}>
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!rules.loading && !rules.data?.length ? (
        <Empty title="No rules" hint="Connections are accepted from anywhere." />
      ) : (
        <div className="panel">
          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr><th>Range</th><th>Applies to</th><th>Effect</th><th>Note</th><th style={{ width: 90 }} /></tr>
              </thead>
              <tbody>
                {(rules.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.cidr}</td>
                    <td className="muted">{r.subject_type === "all" ? "Everyone" : r.subject_name || r.subject_id}</td>
                    <td>
                      <span className={r.allow ? "tag tag--live" : "tag tag--deny"}>
                        {r.allow ? "Allow" : "Block"}
                      </span>
                    </td>
                    <td className="muted small">{r.note}</td>
                    <td className="actions">
                      {canWrite ? (
                        <button
                          className="btn btn--sm btn--ghost"
                          onClick={async () => {
                            await api.del(`/api/console/ip-rules/${r.id}`);
                            rules.reload();
                          }}
                        >
                          Remove
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel__head"><h2>Try an address</h2></div>
        <div className="panel__body">
          <div className="row" style={{ gap: "0.5rem" }}>
            <input
              className="input mono" style={{ maxWidth: 220 }}
              placeholder="203.0.113.5"
              value={probe} onChange={(e) => setProbe(e.target.value)}
            />
            <button
              className="btn btn--sm" disabled={!probe.trim()}
              onClick={async () => {
                setVerdict(await api.post<{ allow: boolean; reason: string }>(
                  "/api/console/ip-rules/test", { address: probe }));
              }}
            >
              Check it
            </button>
            {verdict ? (
              <span className={verdict.allow ? "tag tag--live" : "tag tag--deny"}>
                {verdict.allow ? "Allowed" : verdict.reason}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><h2>How long a session may last</h2></div>
        <div className="panel__body">
          <p className="faint small" style={{ marginBottom: "0.625rem" }}>
            A maximum length, not an idle timeout &mdash; the server sees when a
            connection opened, not whether anyone is touching the keyboard. Anything
            still open past this is ended. Leave it empty for no limit.
          </p>
          <div className="row" style={{ gap: "0.5rem" }}>
            <input
              className="input" style={{ maxWidth: 120 }}
              inputMode="numeric"
              placeholder={limit || "no limit"}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value.replace(/[^0-9]/g, ""))}
            />
            <span className="muted small">minutes</span>
            <button
              className="btn btn--sm" disabled={!canWrite}
              onClick={async () => {
                await api.post("/api/console/settings", { max_session_minutes: minutes || "0" });
                settings.reload();
              }}
            >
              Save
            </button>
            {limit && limit !== "0" ? (
              <span className="muted small">Currently {limit} minutes</span>
            ) : (
              <span className="muted small">Currently no limit</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// --------------------------------------------------------- automation ----

function Tokens({ canWrite }: { canWrite: boolean }) {
  const tokens = useAsync(() => api.get<ApiTokenRow[]>("/api/console/api-tokens"), []);
  /*
   * The same list roles are built from: a token's scopes are console
   * permissions. It arrives as { console, control }; `control` is about what
   * may be done *in a session*, which a token can never open, so only the
   * console half is offered here.
   */
  const available = useAsync(
    () => api.get<{ console: string[]; control: string[] }>(
      "/api/console/roles/available-permissions"), []);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [ttl, setTtl] = useState("");
  const [issued, setIssued] = useState<{ name: string; token: string } | null>(null);
  const [error, setError] = useState("");

  return (
    <>
      {tokens.error ? <Notice>{tokens.error}</Notice> : null}
      {error ? <Notice>{error}</Notice> : null}

      <div className="notice">
        A token lets a script use this server without signing in as a person. It
        carries only the permissions you give it, and what it does is recorded
        against the token rather than against you. It cannot be used to connect to
        a machine &mdash; only to the console API.
      </div>

      {issued ? (
        <div className="notice notice--ok">
          <strong>{issued.name}</strong> — copy this now, it is not shown again.
          <pre className="codeblock" style={{ marginTop: "0.5rem" }}>{issued.token}</pre>
          <div className="row">
            <button
              className="btn btn--sm"
              onClick={() => void navigator.clipboard?.writeText(issued.token)}
            >
              Copy
            </button>
            <button className="btn btn--ghost btn--sm" onClick={() => setIssued(null)}>Done</button>
          </div>
        </div>
      ) : null}

      {canWrite ? (
        <div className="panel">
          <div className="panel__head"><h2>New token</h2></div>
          <div className="panel__body">
            <div className="stack">
              <div className="row row--wrap" style={{ gap: "0.5rem" }}>
                <input
                  className="input" style={{ maxWidth: 240 }}
                  placeholder="What it is for, e.g. nightly inventory"
                  value={name} onChange={(e) => setName(e.target.value)}
                />
                <input
                  className="input" style={{ maxWidth: 150 }}
                  inputMode="numeric" placeholder="Expires in days"
                  value={ttl} onChange={(e) => setTtl(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <button
                  className="btn btn--primary btn--sm"
                  disabled={!name.trim() || !scopes.length}
                  onClick={async () => {
                    setError("");
                    try {
                      const made = await api.post<{ name: string; token: string }>(
                        "/api/console/api-tokens",
                        { name, scopes, ttl_days: Number(ttl) || 0 },
                      );
                      setIssued(made);
                      setName(""); setScopes([]); setTtl("");
                      tokens.reload();
                    } catch (e) { setError((e as Error).message); }
                  }}
                >
                  Create
                </button>
              </div>
              <div className="row row--wrap" style={{ gap: "0.375rem" }}>
                {(available.data?.console ?? []).map((perm) => (
                  <label key={perm} className="row small" style={{ gap: "0.25rem" }}>
                    <input
                      type="checkbox"
                      checked={scopes.includes(perm)}
                      onChange={(e) => setScopes((s) =>
                        e.target.checked ? [...s, perm] : s.filter((x) => x !== perm))}
                    />
                    <span className="mono">{perm}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!tokens.loading && !tokens.data?.length ? (
        <Empty title="No tokens" hint="Create one to let a script use this server." />
      ) : (
        <div className="panel">
          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th><th>Starts with</th><th>Can do</th>
                  <th>Last used</th><th>Expires</th><th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {(tokens.data ?? []).map((t) => (
                  <tr key={t.id}>
                    <td>
                      {t.name}
                      {t.revoked ? <span className="tag tag--deny" style={{ marginLeft: 8 }}>Revoked</span> : null}
                    </td>
                    <td className="mono small">{t.prefix}…</td>
                    <td className="muted small">{t.scopes.join(", ")}</td>
                    <td className="muted small">
                      {t.last_used_at ? relativeTime(t.last_used_at) : "never"}
                    </td>
                    <td className="muted small">
                      {t.expires_at ? dateTime(t.expires_at) : "never"}
                    </td>
                    <td className="actions">
                      {canWrite && !t.revoked ? (
                        <button
                          className="btn btn--sm btn--ghost"
                          onClick={async () => {
                            await api.del(`/api/console/api-tokens/${t.id}`);
                            tokens.reload();
                          }}
                        >
                          Revoke
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}


// ------------------------------------------------------------- alerts ----

/**
 * Being told when something happens.
 *
 * Delivered by webhook, which Slack, Teams, a SIEM and a script all accept.
 * There is no email: it would mean SMTP configuration and a second thing to
 * debug, and all of those can be reached through a webhook already.
 */
function Alerts({ canWrite }: { canWrite: boolean }) {
  const rules = useAsync(() => api.get<any[]>("/api/console/alert-rules"), []);
  const events = useAsync(
    () => api.get<Array<{ id: string; label: string }>>("/api/console/alert-events"), []);
  const sent = useAsync(() => api.get<any[]>("/api/console/alert-deliveries"), []);
  const [name, setName] = useState("");
  const [event, setEvent] = useState("");
  const [target, setTarget] = useState("");
  const [gap, setGap] = useState("300");
  const [error, setError] = useState("");

  return (
    <>
      {rules.error ? <Notice>{rules.error}</Notice> : null}
      {error ? <Notice>{error}</Notice> : null}

      <div className="notice">
        Each alert posts a small JSON message to an address you give it &mdash; a
        Slack or Teams incoming webhook, or anything of your own. The minimum gap
        stops one bad night becoming a thousand messages.
      </div>

      {canWrite ? (
        <div className="panel">
          <div className="panel__body">
            <div className="row row--wrap" style={{ gap: "0.5rem" }}>
              <input
                className="input" style={{ maxWidth: 200 }} placeholder="Name"
                value={name} onChange={(e) => setName(e.target.value)}
              />
              <select
                className="input" style={{ maxWidth: 280 }}
                value={event} onChange={(e) => setEvent(e.target.value)}
              >
                <option value="">Tell me when&hellip;</option>
                {(events.data ?? []).map((e) => (
                  <option key={e.id} value={e.id}>{e.label}</option>
                ))}
              </select>
              <input
                className="input" style={{ flex: 1, minWidth: 200 }}
                placeholder="https://hooks.slack.com/services/…"
                value={target} onChange={(e) => setTarget(e.target.value)}
              />
              <input
                className="input" style={{ maxWidth: 110 }} inputMode="numeric"
                value={gap} onChange={(e) => setGap(e.target.value.replace(/[^0-9]/g, ""))}
              />
              <span className="muted small">seconds apart</span>
              <button
                className="btn btn--primary btn--sm"
                disabled={!name.trim() || !event || !target.trim()}
                onClick={async () => {
                  setError("");
                  try {
                    await api.post("/api/console/alert-rules", {
                      name, event, target, min_interval_seconds: Number(gap) || 0,
                    });
                    setName(""); setTarget("");
                    rules.reload();
                  } catch (e) { setError((e as Error).message); }
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!rules.loading && !rules.data?.length ? (
        <Empty title="No alerts" hint="Nothing is being sent anywhere." />
      ) : (
        <div className="panel">
          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr><th>Name</th><th>When</th><th>Sent to</th><th>Gap</th><th style={{ width: 160 }} /></tr>
              </thead>
              <tbody>
                {(rules.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.name}
                      {r.enabled ? null : <span className="tag" style={{ marginLeft: 8 }}>Off</span>}
                    </td>
                    <td className="muted small">
                      {(events.data ?? []).find((e) => e.id === r.event)?.label ?? r.event}
                    </td>
                    <td className="mono small truncate" style={{ maxWidth: 240 }}>{r.target}</td>
                    <td className="num muted small">{r.min_interval_seconds}s</td>
                    <td className="actions">
                      {canWrite ? (
                        <>
                          <button
                            className="btn btn--sm"
                            onClick={async () => {
                              await api.post(`/api/console/alert-rules/${r.id}/test`);
                              setTimeout(() => sent.reload(), 1200);
                            }}
                          >
                            Test
                          </button>
                          <button
                            className="btn btn--sm"
                            onClick={async () => {
                              await api.patch(`/api/console/alert-rules/${r.id}`,
                                { enabled: !r.enabled });
                              rules.reload();
                            }}
                          >
                            {r.enabled ? "Turn off" : "Turn on"}
                          </button>
                          <button
                            className="btn btn--sm btn--ghost"
                            onClick={async () => {
                              await api.del(`/api/console/alert-rules/${r.id}`);
                              rules.reload();
                            }}
                          >
                            Remove
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel__head">
          <h2>Recently sent</h2>
          <button className="btn btn--sm" onClick={sent.reload}>Refresh</button>
        </div>
        {!sent.data?.length ? (
          <div className="panel__body"><p className="faint small">Nothing yet.</p></div>
        ) : (
          <div className="tablewrap">
            <table className="data">
              <thead><tr><th>When</th><th>Alert</th><th>What happened</th><th>Delivered</th></tr></thead>
              <tbody>
                {(sent.data ?? []).slice(0, 50).map((d) => (
                  <tr key={d.id}>
                    <td className="small nowrap faint">{dateTime(d.created_at)}</td>
                    <td className="muted small">{d.rule_name}</td>
                    <td className="truncate" style={{ maxWidth: 320 }}>{d.summary}</td>
                    <td>
                      {d.ok
                        ? <span className="tag tag--live">Yes</span>
                        : <span className="tag tag--deny" title={d.error}>{d.error || "No"}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
